import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Plus, Edit, Trash2, X, Image, Eye, EyeOff, Upload, FolderPlus, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../AuthContext';
import { galleryApi } from '../../services/api';
import { supabase } from '../../supabaseClient';

const AdminGalleryPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();

  // Auth
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Categories
  const [categories, setCategories] = useState([]);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', sort_order: 0 });
  const [savingCategory, setSavingCategory] = useState(false);

  // Images
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [categoryImages, setCategoryImages] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Image edit dialog
  const [editingImage, setEditingImage] = useState(null);
  const [imageForm, setImageForm] = useState({ alt: '', sort_order: 0 });
  const [savingImage, setSavingImage] = useState(false);

  // Delete confirmations
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'category'|'image', id, name }

  // ─── Auth & fetch ───

  const fetchCategories = useCallback(async (authToken) => {
    try {
      setLoading(true);
      const data = await galleryApi.getCategories();
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching gallery categories:', err);
      toast({ title: 'Error', description: 'Failed to load gallery categories', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    (async () => {
      const { data } = await supabase.auth.getSession();
      const t = data?.session?.access_token;
      if (!t) { navigate('/login'); return; }
      setToken(t);
      fetchCategories(t);
    })();
  }, [isAuthenticated, navigate, fetchCategories]);

  // ─── Category CRUD ───

  const openCategoryForm = (cat = null) => {
    if (cat) {
      setEditingCategory(cat);
      setCategoryForm({ name: cat.name, description: cat.description || '', sort_order: cat.sort_order || 0 });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '', sort_order: categories.length });
    }
    setShowCategoryForm(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) return;
    setSavingCategory(true);
    try {
      if (editingCategory) {
        await galleryApi.updateCategory(editingCategory.id, categoryForm, token);
        toast({ title: 'Category updated' });
      } else {
        await galleryApi.createCategory(categoryForm, token);
        toast({ title: 'Category created' });
      }
      setShowCategoryForm(false);
      fetchCategories(token);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteTarget || deleteTarget.type !== 'category') return;
    try {
      await galleryApi.deleteCategory(deleteTarget.id, token);
      toast({ title: 'Category deleted' });
      setDeleteTarget(null);
      fetchCategories(token);
      if (expandedCategory === deleteTarget.id) setExpandedCategory(null);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  // ─── Image loading ───

  const toggleCategory = async (catId) => {
    if (expandedCategory === catId) {
      setExpandedCategory(null);
      return;
    }
    setExpandedCategory(catId);
    if (!categoryImages[catId]) {
      await fetchImagesForCategory(catId);
    }
  };

  const fetchImagesForCategory = async (catId) => {
    setLoadingImages(prev => ({ ...prev, [catId]: true }));
    try {
      const data = await galleryApi.adminGetImages(catId, token);
      setCategoryImages(prev => ({ ...prev, [catId]: data || [] }));
    } catch (err) {
      console.error('Error fetching images:', err);
    } finally {
      setLoadingImages(prev => ({ ...prev, [catId]: false }));
    }
  };

  // ─── Image upload ───

  const handleImageUpload = async (catId, files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        // 1. Upload file to storage
        const uploadResult = await galleryApi.uploadImage(file, token);
        if (!uploadResult.success) {
          toast({ title: 'Upload failed', description: uploadResult.detail || 'Unknown error', variant: 'destructive' });
          continue;
        }
        // 2. Create image record in DB
        await galleryApi.createImage({
          category_id: catId,
          url: uploadResult.url,
          alt: file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '),
          sort_order: (categoryImages[catId]?.length || 0),
          is_live: false
        }, token);
      }
      toast({ title: `${files.length} image(s) uploaded` });
      await fetchImagesForCategory(catId);
      // Also refresh categories to update counts
      fetchCategories(token);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ─── Image toggle live ───

  const handleToggleLive = async (imageId, catId) => {
    try {
      await galleryApi.toggleLive(imageId, token);
      await fetchImagesForCategory(catId);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  // ─── Image edit ───

  const openImageEdit = (img) => {
    setEditingImage(img);
    setImageForm({ alt: img.alt || '', sort_order: img.sort_order || 0 });
  };

  const handleSaveImage = async () => {
    if (!editingImage) return;
    setSavingImage(true);
    try {
      await galleryApi.updateImage(editingImage.id, imageForm, token);
      toast({ title: 'Image updated' });
      setEditingImage(null);
      await fetchImagesForCategory(editingImage.category_id);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSavingImage(false);
    }
  };

  // ─── Image delete ───

  const handleDeleteImage = async () => {
    if (!deleteTarget || deleteTarget.type !== 'image') return;
    try {
      await galleryApi.deleteImage(deleteTarget.id, token);
      toast({ title: 'Image deleted' });
      setDeleteTarget(null);
      if (deleteTarget.categoryId) {
        await fetchImagesForCategory(deleteTarget.categoryId);
        fetchCategories(token);
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  // ─── Render ───

  if (!isAuthenticated) return null;

  if (loading) {
    return (
      <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="animate-spin" size={40} style={{ color: 'var(--olive-primary)' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
      <div className="container py-8 max-w-5xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="outline" size="sm" style={{ borderRadius: 0 }}>
              <ArrowLeft size={16} className="mr-2" /> Back
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="heading-2">Gallery Management</h1>
            <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
              Manage categories and images for the public gallery
            </p>
          </div>
          <Button onClick={() => openCategoryForm()} className="btn-primary flex items-center gap-2">
            <FolderPlus size={16} /> New Category
          </Button>
        </div>

        {/* Categories list */}
        {categories.length === 0 ? (
          <div className="p-12 text-center" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}>
            <Image size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
            <p className="heading-4 mb-2">No Gallery Categories</p>
            <p className="body-small mb-6" style={{ color: 'var(--text-secondary)' }}>
              Create your first category to start adding images.
            </p>
            <Button onClick={() => openCategoryForm()} className="btn-primary">
              <Plus size={16} className="mr-2" /> Create Category
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {categories.map((cat) => (
              <div
                key={cat.id}
                style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-light)' }}
              >
                {/* Category header row */}
                <div
                  className="flex items-center justify-between p-6 cursor-pointer"
                  onClick={() => toggleCategory(cat.id)}
                >
                  <div className="flex items-center gap-4">
                    {expandedCategory === cat.id
                      ? <ChevronUp size={20} style={{ color: 'var(--text-secondary)' }} />
                      : <ChevronDown size={20} style={{ color: 'var(--text-secondary)' }} />
                    }
                    <div>
                      <h3 className="heading-4">{cat.name}</h3>
                      {cat.description && (
                        <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{cat.description}</p>
                      )}
                    </div>
                    <span
                      className="body-small px-3 py-1"
                      style={{ backgroundColor: 'rgba(95,111,82,0.1)', color: 'var(--olive-primary)' }}
                    >
                      {cat.image_count || 0} image{(cat.image_count || 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => openCategoryForm(cat)}
                      style={{ borderRadius: 0 }}
                    >
                      <Edit size={14} />
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setDeleteTarget({ type: 'category', id: cat.id, name: cat.name })}
                      style={{ borderRadius: 0, borderColor: '#dc2626', color: '#dc2626' }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>

                {/* Expanded: images panel */}
                {expandedCategory === cat.id && (
                  <div className="px-6 pb-6" style={{ borderTop: '1px solid var(--border-light)' }}>
                    {/* Upload bar */}
                    <div className="flex items-center gap-4 py-4">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => handleImageUpload(cat.id, e.target.files)}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={uploading}
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-2"
                        style={{ borderRadius: 0 }}
                      >
                        {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                        Upload Images
                      </Button>
                      <span className="body-small" style={{ color: 'var(--text-muted)' }}>
                        Max 5MB per image. PNG, JPG, WEBP supported.
                      </span>
                    </div>

                    {/* Images grid */}
                    {loadingImages[cat.id] ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="animate-spin" size={24} style={{ color: 'var(--olive-primary)' }} />
                      </div>
                    ) : !categoryImages[cat.id] || categoryImages[cat.id].length === 0 ? (
                      <div className="text-center py-8">
                        <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                          No images in this category yet. Upload some above.
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {categoryImages[cat.id].map((img) => (
                          <div
                            key={img.id}
                            className="relative group"
                            style={{
                              border: `2px solid ${img.is_live ? 'var(--olive-primary)' : 'var(--border-light)'}`,
                              overflow: 'hidden'
                            }}
                          >
                            <img
                              src={img.url}
                              alt={img.alt || ''}
                              className="w-full h-40 object-cover"
                            />
                            {/* Live badge */}
                            <div
                              className="absolute top-2 left-2 px-2 py-1 body-small"
                              style={{
                                backgroundColor: img.is_live ? 'var(--olive-primary)' : 'rgba(0,0,0,0.6)',
                                color: '#fff',
                                fontSize: '11px',
                                fontWeight: 600
                              }}
                            >
                              {img.is_live ? 'LIVE' : 'DRAFT'}
                            </div>
                            {/* Action overlay */}
                            <div
                              className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
                              style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                            >
                              <Button
                                variant="outline" size="sm"
                                onClick={() => handleToggleLive(img.id, cat.id)}
                                style={{ borderRadius: 0, backgroundColor: 'rgba(255,255,255,0.9)', borderColor: 'transparent' }}
                                title={img.is_live ? 'Set to Draft' : 'Set to Live'}
                              >
                                {img.is_live ? <EyeOff size={14} /> : <Eye size={14} />}
                              </Button>
                              <Button
                                variant="outline" size="sm"
                                onClick={() => openImageEdit(img)}
                                style={{ borderRadius: 0, backgroundColor: 'rgba(255,255,255,0.9)', borderColor: 'transparent' }}
                              >
                                <Edit size={14} />
                              </Button>
                              <Button
                                variant="outline" size="sm"
                                onClick={() => setDeleteTarget({ type: 'image', id: img.id, name: img.alt || 'this image', categoryId: cat.id })}
                                style={{ borderRadius: 0, backgroundColor: 'rgba(255,255,255,0.9)', borderColor: 'transparent', color: '#dc2626' }}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                            {/* Alt text */}
                            {img.alt && (
                              <div className="p-2" style={{ backgroundColor: 'var(--bg-primary)' }}>
                                <p className="body-small truncate" style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                                  {img.alt}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── Category Form Dialog ─── */}
      <Dialog open={showCategoryForm} onOpenChange={setShowCategoryForm}>
        <DialogContent style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}>
          <DialogHeader>
            <DialogTitle className="heading-3">
              {editingCategory ? 'Edit Category' : 'New Category'}
            </DialogTitle>
            <DialogDescription className="body-small" style={{ color: 'var(--text-secondary)' }}>
              {editingCategory ? 'Update this gallery category.' : 'Create a new gallery category to group images.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="body-small font-medium">Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={e => setCategoryForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Rooms, Facilities, Exterior"
                style={{ borderRadius: 0 }}
              />
            </div>
            <div>
              <Label className="body-small font-medium">Description</Label>
              <Textarea
                value={categoryForm.description}
                onChange={e => setCategoryForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Optional description"
                style={{ borderRadius: 0 }}
                rows={2}
              />
            </div>
            <div>
              <Label className="body-small font-medium">Sort Order</Label>
              <Input
                type="number"
                value={categoryForm.sort_order}
                onChange={e => setCategoryForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))}
                style={{ borderRadius: 0 }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryForm(false)} style={{ borderRadius: 0 }}>
              Cancel
            </Button>
            <Button
              className="btn-primary"
              onClick={handleSaveCategory}
              disabled={savingCategory || !categoryForm.name.trim()}
            >
              {savingCategory ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Image Edit Dialog ─── */}
      <Dialog open={!!editingImage} onOpenChange={(open) => { if (!open) setEditingImage(null); }}>
        <DialogContent style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}>
          <DialogHeader>
            <DialogTitle className="heading-3">Edit Image</DialogTitle>
            <DialogDescription className="body-small" style={{ color: 'var(--text-secondary)' }}>
              Update alt text and sort order for this image.
            </DialogDescription>
          </DialogHeader>
          {editingImage && (
            <div className="space-y-4 py-4">
              <img src={editingImage.url} alt={editingImage.alt} className="w-full h-48 object-cover" style={{ border: '1px solid var(--border-light)' }} />
              <div>
                <Label className="body-small font-medium">Alt Text</Label>
                <Input
                  value={imageForm.alt}
                  onChange={e => setImageForm(p => ({ ...p, alt: e.target.value }))}
                  placeholder="Describe the image"
                  style={{ borderRadius: 0 }}
                />
              </div>
              <div>
                <Label className="body-small font-medium">Sort Order</Label>
                <Input
                  type="number"
                  value={imageForm.sort_order}
                  onChange={e => setImageForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))}
                  style={{ borderRadius: 0 }}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingImage(null)} style={{ borderRadius: 0 }}>
              Cancel
            </Button>
            <Button className="btn-primary" onClick={handleSaveImage} disabled={savingImage}>
              {savingImage ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Delete Confirmation ─── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="heading-3">
              Delete {deleteTarget?.type === 'category' ? 'Category' : 'Image'}?
            </AlertDialogTitle>
            <AlertDialogDescription className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              {deleteTarget?.type === 'category'
                ? `Are you sure you want to delete "${deleteTarget?.name}"? The category must have no images.`
                : `Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderRadius: 0 }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteTarget?.type === 'category' ? handleDeleteCategory : handleDeleteImage}
              style={{ borderRadius: 0, backgroundColor: '#dc2626' }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminGalleryPage;
